import { useEffect, useRef } from 'react';
import { presenceStore } from '@/features/presence/presenceStore';
import type { PresenceState } from '@/features/presence/types';
import { useStoreValue } from '@/lib/tinyStore';
import { publishCallActivity } from '../callWindow';
import type { DesktopCallActivity } from '../types';

const select = (state: PresenceState): DesktopCallActivity => ({
  inCall: state.flags.inCall,
  presenting: state.flags.presenting,
  screenSharing: state.flags.screenSharing,
});

const same = (a: DesktopCallActivity, b: DesktopCallActivity): boolean =>
  a.inCall === b.inCall &&
  a.presenting === b.presenting &&
  a.screenSharing === b.screenSharing;

/**
 * How long a fresh document may stay silent before its silence is taken as an
 * answer. Long enough for `useCallGrant` plus a join to land many times over,
 * short enough that a window which is never going to re-establish its call
 * cannot pin the workspace on "In a call" until somebody closes it.
 */
const MOUNT_SILENCE_MS = 8_000;

/**
 * THE CALL WINDOW'S HALF OF PRESENCE — mounted only there.
 *
 * A person has ONE presence and one heartbeat, and both live in
 * `PresenceRuntime`, which `AppOverlays` deliberately does not mount in a call
 * window: two idle monitors for one human disagree about whether they are here.
 * So this document sets `flags.inCall` and reports nothing, while the document
 * that reports never learns there is a call — and the workspace, receiving no
 * pointer or key input for the whole call, recomputed `autoState` as `away` and
 * wrote it over the `in_a_call` the server had sent down.
 *
 * WHAT THAT COSTS, EXACTLY. The caller's OWN chip and OWN tray icon flipped to
 * Away in the middle of their own call. That is the whole of it, and it is
 * worth saying because the first version of this comment claimed something much
 * larger — that "In a call" was never sent to anybody and that infra/sql/067
 * had come back through the client — and that was WRONG in every link.
 * `presenceApi.heartbeat()` is an empty function with no route to receive it,
 * and `activity_state` is derived by trigger from `call_participants`
 * (infra/sql/062, "Never client-set") and pinned into tier 1 by 063 and the
 * gateway relay. Colleagues saw In a call throughout. This bus is a LOCAL
 * reconciliation, never a presence input; the greps are in the shell's
 * `shared/callActivity.ts`.
 *
 * It reads THIS document's own flags rather than inventing a signal, so
 * everything that already sets them keeps working unchanged and cannot drift:
 * `useCallLifecycle` owns `inCall` for every way into and out of a call, and
 * `useSharePresence` (through `useMediaPreferences`, which both live rooms hand
 * their client to) owns presenting/screenSharing for every way a share starts
 * or stops — including the browser's own "Stop sharing" bar.
 *
 * A no-op outside a desktop call window: `publishCallActivity` checks, and the
 * shell refuses a report from any other window regardless.
 */
export function useDesktopCallActivity(): void {
  const activity = useStoreValue(presenceStore, select, same);
  /**
   * THE FIRST THING A FRESH DOCUMENT KNOWS IS NOTHING, AND NOTHING IS NOT NEWS.
   *
   * This effect runs on mount with whatever the store held at render time,
   * which on a fresh document is all-false — `useCallLifecycle`'s
   * `setFlags({inCall: true})` has not run yet. Published, that clears the row
   * the shell is still holding for this same `webContents` from before a reload,
   * so the aggregate goes true → false → true within a frame; a presence tick
   * landing in that gap reports the person as available in the middle of their
   * own call, which is the one thing this whole bus exists to prevent.
   *
   * So the opening all-false is withheld: it is the ABSENCE of knowledge, not
   * knowledge. Every all-false after it is a real transition — the call ended —
   * and is published normally.
   */
  const spoken = useRef(false);

  useEffect(() => {
    if (!spoken.current && !activity.inCall && !activity.presenting && !activity.screenSharing) {
      return;
    }
    spoken.current = true;
    publishCallActivity(activity);
  }, [activity]);

  /**
   * BUT SILENCE HAS TO EXPIRE, OR "not yet" BECOMES "never".
   *
   * `webContents.id` survives a reload, so the shell's row for this window is
   * neither pruned nor dropped while the window lives; the ONLY other thing
   * that clears it is `forgetCallActivity` when the window closes. So a call
   * window reloaded after its call ended — Ctrl+R still works, the app menu is
   * global and `autoHideMenuBar` hides the bar without disarming the
   * accelerator — that then fails to re-establish its call (`useCallGrant` is
   * `staleTime: Infinity, retry: false` and cannot self-heal) never publishes
   * anything at all, and the workspace's own chip and the tray read "In a call"
   * INDEFINITELY. That is the same stuck-forever shape the shell's `reportAlive`
   * was just fixed to prevent, one file over.
   *
   * A document that has genuinely not learned anything yet still says nothing —
   * which is the whole point of the guard above — but only for as long as
   * "yet" is credible. The direction stays fail-safe throughout: stale busy,
   * never Available mid-call.
   */
  useEffect(() => {
    if (spoken.current) return undefined;
    const timer = window.setTimeout(() => {
      if (spoken.current) return;
      spoken.current = true;
      publishCallActivity(select(presenceStore.get()));
    }, MOUNT_SILENCE_MS);
    return () => window.clearTimeout(timer);
  }, []);
}
